<script>
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import Button from "@/components/atoms/button";
import LazyImage from "@/components/atoms/lazy-image";
import LinkTo from "@/components/atoms/link-to";
import Form from "@/components/molecules/form";
import FormError from "@/components/molecules/form/form-error";
import CarteFideliteTooltip from "@/components/organisms/carte-fidelite-form/tooltip";
import RetrouverPorteur from "@/components/organisms/carte-fidelite-form/retrouver-porteur";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";

import carteCoraAvantage from "@/assets/images/carte-cora-avantage.png";
import cartesMalinEtMastercard from "@/assets/images/carte-mastercard-et-carte-malin.png";
import CarteCora from "@/assets/images/carte-cora.png";
import CarteMalin from "@/assets/images/carte-malin.png";

import { numCarteFidelite } from "@/utils/form-validators";
import { required } from "@vuelidate/validators";
import { isCarteCora } from "@/utils/fidelite";

export default {
  name: "CarteFideliteForm",
  components: {
    Form,
    FormInput,
    FormGroup,
    Button,
    LazyImage,
    CarteFideliteTooltip,
    FormError,
    LinkTo,
    RetrouverPorteur
  },
  props: {
    context: {
      type: String,
      required: false,
      default: "addCard"
    },
    typeCarte: {
      type: String,
      required: false,
      default: ""
    },
    isEligibleDefis: {
      type: Boolean,
      required: false,
      default: false
    },
    isErrorCarteAssociee: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  emits: ["saveCarteFidelite"],
  setup(props) {
    const { v$, getState, getErrorMessage, checkValidation } =
      useFormValidation(props);
    const numeroCarte1Message = ref(null);
    return {
      carteCoraAvantage,
      CarteCora,
      CarteMalin,
      cartesMalinEtMastercard,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      numeroCarte1Message
    };
  },
  data() {
    return {
      num_carte: "",
      nom: "",
      prenom: ""
    };
  },
  methods: {
    updateCarteFidelite() {
      if (this.checkValidation(this.v$)) {
        this.$emit("saveCarteFidelite", {
          num_carte: this.num_carte,
          nom: this.nom,
          prenom: this.prenom
        });
      }
    },
    resetInput() {
      this.v$.num_carte.$reset();
    }
  },
  validations() {
    return {
      num_carte: {
        isValid(value) {
          return numCarteFidelite(value);
        }
      },
      nom: {
        required
      },
      prenom: {
        required
      }
    };
  },
  computed: {
    isAddCard() {
      if (this.context === "addCard") {
        return true;
      } else {
        return false;
      }
    },
    imageFormCarte() {
      if (this.v$.num_carte.$invalid || this.num_carte === "") {
        return null;
      } else if (isCarteCora(this.num_carte)) {
        return CarteCora;
      } else {
        return CarteMalin;
      }
    }
  }
};
</script>

<template>
  <Form
    class="c-fidelity-card-form"
    @submit.prevent="updateCarteFidelite"
  >
    <div class="u-flex u-flex-column u-flex-align-center">
      <LazyImage
        :src="cartesMalinEtMastercard"
        class="u-mb-xs u-inline-block"
        alt="Carte Cora & carte Malin"
        width="91"
        height="69"
      />
      <h2 class="u-font-secondary u-color-primary u-font-24 u-font-700 u-mb-xxs">
        {{ isAddCard ? 'Enregistrez ma carte' : 'Modifiez votre carte de fidelité' }}
      </h2>
      <p class="u-text-center u-font-16 u-mb-lg">
        Consultez et profitez de vos avantages à tout moment&nbsp;!
      </p>
      <div>
        <FormGroup
          class="u-mb-xs"
          size="md"
          :state="getState(v$,'num_carte_' + typeCarte) || getState(v$,'num_carte')"
        >
          <FormInput
            id="numero"
            name="numero"
            v-model="num_carte"
            :state="getState(v$,'num_carte_' + typeCarte) || getState(v$,'num_carte')"
            size="md"
            :img-path="imageFormCarte"
            :aria-describedby="numeroCarte1Message?.id"
            img-alt=""
            @input="resetInput"
            @blur="v$.num_carte.$touch()"
          >
            N° de fidélité
          </FormInput>

          <template #message>
            <p
              ref="numeroCarte1Message"
              v-uid
            >
              {{ getErrorMessage('num_carte_' + typeCarte, "Merci de renseigner un numéro de carte fidélité valide") }}
              <template v-if="getErrorMessage('num_carte_'+ typeCarte).includes('pas active') ">
                Pour l’activer <LinkTo
                  :to="'/infos-et-services/aide/contactez-nous/formulaire-activation-carte-'+ typeCarte"
                  target="_blank"
                >
                  cliquez-ici
                </LinkTo>
              </template>
            </p>
          </template>
        </FormGroup>

        <CarteFideliteTooltip :is-eligible-defis="isEligibleDefis" />

        <FormGroup
          class="u-my-sm"
          size="md"
          :state="getState(v$,'nom_porteur_carte_' + typeCarte) || getState(v$,'nom')"
        >
          <FormInput
            id="nom"
            v-model="nom"
            size="md"
            :state="getState(v$,'nom_porteur_carte_' + typeCarte) || getState(v$,'nom')"
          >
            Nom du porteur
          </FormInput>

          <template #message>
            <p>
              {{ getErrorMessage('nom_porteur_carte_' + typeCarte,"Merci de renseigner le nom du porteur de la carte de fidelité") }}
            </p>
          </template>
        </FormGroup>
        <FormGroup
          class="u-my-sm"
          size="md"
          :state="getState(v$,'prenom_porteur_carte_' + typeCarte) || getState(v$,'prenom')"
        >
          <FormInput
            id="prenom"
            v-model="prenom"
            size="md"
            :state="getState(v$,'prenom_porteur_carte_' + typeCarte) || getState(v$,'prenom')"
          >
            Prénom du porteur
          </FormInput>

          <template #message>
            <p>
              {{ getErrorMessage('prenom_porteur_carte_' + typeCarte,"Merci de renseigner le prénom du porteur de la carte de fidelité") }}
            </p>
          </template>
        </FormGroup>

        <RetrouverPorteur :is-eligible-defis="isEligibleDefis" />
      </div>
    </div>
    <FormError
      v-if="error"
      :error="error"
      class="u-mt-md u-text-center"
    />
    <Button
      v-if="isErrorCarteAssociee"
      to="/infos-et-services/pages-cachees/carte-fidelite-associee-a-un-compte-comment-faire"
      target="_blank"
      link
      class="u-mt-xs"
    >Aide</Button>
    <div class="u-mt-md u-mb-xlg u-flex u-flex-justify-center">
      <Button
        radius="full"
        variant="primary"
        size="md"
        value="J’enregistre ma carte"
        type="submit"
        :disabled="!num_carte || !nom || !prenom"
      />
    </div>
  </Form>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/form-fidelity-card.scss";
</style>
